import * as React from 'react';
import * as ScrollAreaPrimitive from '@radix-ui/react-scroll-area';
import {cn} from '../lib/utils';

interface ScrollAreaProps
  extends React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.Root> {
  automaticallyScrollToLastItem?: boolean;
  viewportRef?: React.Ref<HTMLDivElement>;
}

const ScrollArea = React.forwardRef<
  React.ElementRef<typeof ScrollAreaPrimitive.Root>,
  ScrollAreaProps
>(
  (
    {
      className,
      children,
      automaticallyScrollToLastItem,
      viewportRef: viewportRefProp,
      ...props
    },
    ref,
  ) => {
    const internalViewportRef = React.useRef<HTMLDivElement | null>(null);

    const setViewportRef = React.useCallback(
      (node: HTMLDivElement | null) => {
        internalViewportRef.current = node;
        if (typeof viewportRefProp === 'function') {
          viewportRefProp(node);
        } else if (viewportRefProp) {
          (
            viewportRefProp as React.MutableRefObject<HTMLDivElement | null>
          ).current = node;
        }
      },
      [viewportRefProp],
    );

    React.useEffect(() => {
      if (!automaticallyScrollToLastItem || !internalViewportRef.current) {
        return;
      }
      internalViewportRef.current.scrollTo({
        top: internalViewportRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }, [automaticallyScrollToLastItem, children]);

    return (
      <ScrollAreaPrimitive.Root
        ref={ref}
        className={cn('relative overflow-hidden', className)}
        {...props}
      >
        <ScrollAreaPrimitive.Viewport
          ref={setViewportRef}
          // p-0.5 leaves room for the 2px focus ring on full-width children.
          // A scroll container always clips at its padding box, and ring-* is
          // painted outside the border box, so without this the ring is shaved.
          className="h-full w-full rounded-[inherit] p-0.5 *:h-full"
        >
          {children}
        </ScrollAreaPrimitive.Viewport>
        <ScrollBar />
        <ScrollAreaPrimitive.Corner />
      </ScrollAreaPrimitive.Root>
    );
  },
);
ScrollArea.displayName = ScrollAreaPrimitive.Root.displayName;

const ScrollBar = React.forwardRef<
  React.ElementRef<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>,
  React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>
>(({className, orientation = 'vertical', ...props}, ref) => (
  <ScrollAreaPrimitive.ScrollAreaScrollbar
    ref={ref}
    orientation={orientation}
    className={cn(
      'flex touch-none select-none transition-colors',
      orientation === 'vertical' &&
        'h-full w-2.5 border-l border-l-transparent p-px',
      orientation === 'horizontal' &&
        'h-2.5 flex-col border-t border-t-transparent p-px',
      className,
    )}
    {...props}
  >
    <ScrollAreaPrimitive.ScrollAreaThumb className="relative flex-1 rounded-full bg-border" />
  </ScrollAreaPrimitive.ScrollAreaScrollbar>
));
ScrollBar.displayName = ScrollAreaPrimitive.ScrollAreaScrollbar.displayName;

export {ScrollArea, ScrollBar};
